/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { ComposedChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import servicioService from '../services/servicioService';
import { toast } from 'react-toastify';

// Constante fuera del componente para evitar recreación en cada render
const mesesNombres = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [dataMensual, setDataMensual] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();
  const [totalPaidPreviousMonths, setTotalPaidPreviousMonths] = useState(0);

  // function to fetch total paid previous months
  const fetchTotalPaidPreviousMonths = async () => {
    try {
      setLoading(true);
      const data = await servicioService.getTotalPaidPreviousMonths();
      setTotalPaidPreviousMonths(data?.total);
    } catch (error) {
      toast.error('Error al obtener datos de pagos anteriores. Por favor, intenta de nuevo más tarde.');
      setTotalPaidPreviousMonths(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTotalPaidPreviousMonths();
  }, []);

  const fetchMonthlyData = async () => {
    try {
      setLoading(true);
      const yearlyData = await servicioService.getYearlyCompanyStats(currentYear);
      handleSuccess(yearlyData);
      toast.success('Estadísticas anuales obtenidas');
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlyData();
  }, [currentYear]);

  const handleError = (error) => {
    toast.error('Error al obtener datos anuales. Por favor, intenta de nuevo más tarde.');
    setDataMensual(mesesNombres.map(mes => ({
      mes,
      servicios: 0,
      ingreso: 0,
      egreso: 0,
      ganancias: 0
    })));
  }

  const handleSuccess = (yearlyData) => {
    const processedData = yearlyData?.monthlyStats?.map(monthData => {
      const servicios = monthData.totalServiceCount || 0;
      const ingreso = monthData.totalAmountDue || 0;

      return {
        mes: monthData.monthName,
        servicios,
        ingreso,
        egreso: monthData?.totalPaid || 0,
        ganancias: ingreso - monthData?.totalPaid || 0
      };
    });
    setDataMensual(processedData);
  }

  // Calcular totales
  const totales = dataMensual.reduce((acc, mes) => ({
    servicios: acc.servicios + mes.servicios,
    ingreso: acc.ingreso + mes.ingreso
  }), { servicios: 0, ingreso: 0 });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="p-6 h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando datos del año {currentYear}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-0 h-screen overflow-hidden">
      {/* Header con botones */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Dashboard</h2>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/usuarios-empresas')}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-200 flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Usuarios
          </button>
          <button
            onClick={() => navigate('/api-estadisticas')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-200 flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Estadísticas API
          </button>
        </div>
      </div>

      {/* Layout principal: 60% izquierda, 40% derecha */}
      <div className="flex gap-6 h-[calc(100vh-100px)]">
        {/* Columna Izquierda (60%) */}
        <div className="w-[58%] flex flex-col gap-6">
          {/* 3 Cards principales */}
          <div className="grid grid-cols-3 gap-4 ">
            {/* Servicios Totales */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700">Servicios Totales</h3>
                <span className="text-orange-500 bg-orange-100 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-800">{totales.servicios}</p>
              <p className="text-xs text-gray-500 mt-1">Total de servicios realizados</p>
            </div>

            {/* Total Recibido */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700">Total Recibido</h3>
                <span className="text-green-500 bg-green-100 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-800">{formatCurrency(totales.ingreso)}</p>
              <p className="text-xs text-gray-500 mt-1">Ingresos totales</p>
            </div>

            {/* Total Gastado */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700">Total Gastado</h3>
                <span className="text-blue-500 bg-blue-100 p-2 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalPaidPreviousMonths)}</p>
              <p className="text-xs text-gray-500 mt-1">Gastos totales</p>
            </div>
          </div>

          {/* Gráfica Combinada - Ingresos vs Egresos con Línea de Servicios */}
          <div className="bg-white rounded-lg shadow-md p-4 flex-1">
            <h3 className="text-base font-bold text-gray-800 mb-2">Ingresos vs Egresos Mensuales</h3>
            <ResponsiveContainer width="100%" height="95%">
              <ComposedChart data={dataMensual} margin={{ top: 5, right: 20, bottom: 5, left: 0 }} barGap={8} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="mes"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  style={{ fontSize: '10px' }}
                  tick={{ dy: 5 }}
                />
                <YAxis
                  yAxisId="left"
                  style={{ fontSize: '10px' }}
                  width={60}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  style={{ fontSize: '10px' }}
                  width={40}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'Servicios') return [value, name];
                    return [formatCurrency(value), name];
                  }}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px', fontSize: '11px' }}
                />
                <Legend wrapperStyle={{ paddingTop: '5px', fontSize: '11px' }} iconSize={10} />
                <Bar yAxisId="left" dataKey="ingreso" fill="#10b981" name="Ingresos" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="left" dataKey="egreso" fill="#3b82f6" name="Egresos" radius={[4, 4, 0, 0]} />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="servicios"
                  stroke="#f97316"
                  strokeWidth={2.5}
                  name="Servicios"
                  dot={{ fill: '#f97316', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Gráfica de Área - Ganancias con Línea de Servicios */}
          <div className="bg-white rounded-lg shadow-md p-4 flex-1">
            <h3 className="text-base font-bold text-gray-800 mb-2">Evolución de Ganancias y Servicios</h3>
            <ResponsiveContainer width="100%" height="95%">
              <ComposedChart data={dataMensual} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <defs>
                  <linearGradient id="colorGanancias" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="mes"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  style={{ fontSize: '10px' }}
                  tick={{ dy: 5 }}
                />
                <YAxis
                  yAxisId="left"
                  style={{ fontSize: '10px' }}
                  width={60}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  style={{ fontSize: '10px' }}
                  width={40}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'Cantidad Servicios') return [value, name];
                    return [formatCurrency(value), name];
                  }}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px', fontSize: '11px' }}
                />
                <Legend wrapperStyle={{ paddingTop: '5px', fontSize: '11px' }} iconSize={10} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="ganancias"
                  stroke="#8b5cf6"
                  strokeWidth={2.5}
                  fillOpacity={1}
                  fill="url(#colorGanancias)"
                  name="Ganancias"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="servicios"
                  stroke="#f59e0b"
                  strokeWidth={2.5}
                  name="Cantidad Servicios"
                  dot={{ fill: '#f59e0b', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Columna Derecha (40%) - Tabla */}
        <div className="w-[42%] bg-white rounded-lg shadow-md pt-6 px-2 overflow-hidden flex flex-col">
          <h3 className="text-lg font-bold text-gray-800 mb-4 ml-2">Resumen Financiero Anual</h3>
          <div className="overflow-y-auto flex-1">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Mes</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-700">Servicios</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-700">Ingreso</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-700">Egreso</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-700">Ganancias</th>
                </tr>
              </thead>
              <tbody>
                {dataMensual.map((mes, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-3 font-medium text-gray-800">{mes.mes}</td>
                    <td className="px-3 py-3 text-center text-orange-600 font-semibold">{mes.servicios}</td>
                    <td className="px-3 py-3 text-right text-green-600 font-semibold">{formatCurrency(mes.ingreso)}</td>
                    <td className="px-3 py-3 text-right text-blue-600 font-semibold">{formatCurrency(mes.egreso)}</td>
                    <td className="px-3 py-3 text-right text-purple-600 font-semibold">{formatCurrency(mes.ganancias)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100 sticky bottom-0 font-bold">
                <tr>
                  <td className="px-3 py-3 text-gray-800">TOTAL</td>
                  <td className="px-3 py-3 text-center text-orange-700">{totales.servicios}</td>
                  <td className="px-3 py-3 text-right text-green-700">{formatCurrency(totales.ingreso)}</td>
                  <td className="px-3 py-3 text-right text-blue-700">{formatCurrency(totalPaidPreviousMonths)}</td>
                  <td className="px-3 py-3 text-right text-purple-700">{formatCurrency(totales.ingreso - totalPaidPreviousMonths)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
